/**
 * CDP SDK client-side event batching
 *
 * Buffers outgoing events (already consent-filtered and run through beforeSend
 * by processAndSendEvent) and ships them together via POST /v1/events/batch.
 * A single tracked article view fires ~9 events, so batching collapses that to
 * roughly one request — cutting request volume ~9x on both the browser and the
 * single ingest replica.
 *
 * Flush triggers:
 *   - buffer reaches config.batchSize
 *   - config.flushInterval ms elapse since the first buffered event
 *   - identify events flush immediately (so the profile is resolved promptly)
 *   - page hide / unload (beacon flush, see flushBatchOnUnload)
 *
 * On a failed flush, each event falls back to the durable retry queue, so
 * batching never loses events relative to the single-send path.
 */

import type { CDPCoreConfig, EventPayload, AfterSendCallback } from './types';
import { sendBatch, sendBeaconBatch } from './transport';
import { enqueue } from './queue';
import { log } from './utils';

interface BufferEntry {
  payload: EventPayload;
  afterSend: AfterSendCallback[];
}

// The server caps a batch at 100 (MAX_BATCH_SIZE in @cdp/common's
// batchEventSchema); keep flush chunks at that number so a large burst splits
// into several requests rather than a 400.
const MAX_FLUSH_CHUNK = 100;

let buffer: BufferEntry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function clearTimer(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function scheduleFlush(config: CDPCoreConfig): void {
  if (timer) return;
  const wait = config.flushInterval ?? 3000;
  timer = setTimeout(() => {
    timer = null;
    void flushBatch(config);
  }, wait);
  // Don't keep a Node/test process alive purely for the flush timer.
  (timer as unknown as { unref?: () => void }).unref?.();
}

/**
 * Whether batching is enabled for this config. batchSize <= 1 means
 * "send each event immediately" (no buffering).
 */
export function isBatchingEnabled(config: CDPCoreConfig): boolean {
  return (config.batchSize ?? 0) > 1;
}

/**
 * Add a finalized (post-consent, post-beforeSend) event to the batch buffer.
 * Returns true — the event is accepted for delivery. afterSend callbacks fire
 * when the batch flush resolves, with the batch's success flag.
 */
export function bufferEvent(
  payload: EventPayload,
  config: CDPCoreConfig,
  afterSendCallbacks: AfterSendCallback[]
): boolean {
  buffer.push({ payload, afterSend: afterSendCallbacks });

  // Identify should not sit in the buffer — flush right away so later events
  // are attributed to the identified profile.
  if (payload.type === 'identify' || buffer.length >= (config.batchSize ?? 10)) {
    void flushBatch(config);
  } else {
    scheduleFlush(config);
  }
  return true;
}

/**
 * Flush buffered events over the network (fetch + keepalive). Splits into
 * chunks of MAX_FLUSH_CHUNK. Failed chunks fall back to the retry queue.
 */
export async function flushBatch(config: CDPCoreConfig): Promise<void> {
  clearTimer();
  if (buffer.length === 0) return;

  // Claim the whole buffer up front so events added during the await land in a
  // fresh buffer rather than being flushed twice.
  const claimed = buffer;
  buffer = [];

  for (let i = 0; i < claimed.length; i += MAX_FLUSH_CHUNK) {
    const chunk = claimed.slice(i, i + MAX_FLUSH_CHUNK);
    const payloads = chunk.map((e) => e.payload);
    const result = await sendBatch(config.apiHost, config.writeKey, payloads);

    for (const entry of chunk) {
      for (const cb of entry.afterSend) {
        try {
          cb(entry.payload, result.success);
        } catch (e) {
          log('Error in afterSend callback:', e);
        }
      }
      // Retryable or not, a failed batch send should not silently drop the
      // event — hand it to the durable queue, which retries on next page load.
      if (!result.success) {
        enqueue(entry.payload);
      }
    }
  }
}

/**
 * Synchronous best-effort flush for page hide / unload. Uses sendBeacon (which
 * survives the unload) in small chunks to stay under the browser's beacon size
 * cap. Anything the beacon refuses is persisted via the retry queue.
 */
export function flushBatchOnUnload(config: CDPCoreConfig): void {
  clearTimer();
  if (buffer.length === 0) return;

  const claimed = buffer;
  buffer = [];

  // Smaller chunks for beacon: ~64KB cap, events are ~1-2KB each.
  const BEACON_CHUNK = 20;
  for (let i = 0; i < claimed.length; i += BEACON_CHUNK) {
    const chunk = claimed.slice(i, i + BEACON_CHUNK);
    const payloads = chunk.map((e) => e.payload);
    const ok = sendBeaconBatch(config.apiHost, config.writeKey, payloads);
    if (!ok) {
      // Beacon refused (size/unsupported) — persist so next page load retries.
      for (const entry of chunk) enqueue(entry.payload);
    }
  }
}

/** Current number of buffered (not yet flushed) events. */
export function getBatchBufferSize(): number {
  return buffer.length;
}
